import { useMutation } from '@ltvco/refresh-lib/vendors';

import type { AccountSetupModalValues } from './AccountSetupModal';
import type { SignupReasonCode } from './constants';

interface AccountApiErrorResponse {
  account?: {
    errors?: string[];
  };
  errors?: string[];
  message?: string;
  error?: string;
}

async function parseApiError(response: Response): Promise<never> {
  const errorData: AccountApiErrorResponse = await response
    .json()
    .catch(() => ({}));
  const accountErrors = errorData.account?.errors;
  const firstError =
    Array.isArray(accountErrors) && accountErrors.length > 0
      ? accountErrors[0]
      : Array.isArray(errorData.errors) && errorData.errors.length > 0
      ? errorData.errors[0]
      : null;

  throw new Error(
    firstError ||
      errorData.message ||
      errorData.error ||
      `Request failed with status ${response.status}`
  );
}

export interface UpdatePhoneNumberPayload {
  phone_number: string;
  country_dial_code?: string;
}

function buildPhoneUpdatePayload(
  phoneNumber: string,
  countryDialCode: string
): UpdatePhoneNumberPayload {
  const dialDigits = countryDialCode.replace(/\D/g, '');
  const digits = phoneNumber.replace(/\D/g, '');

  let localNumber = digits;

  if (
    phoneNumber.trimStart().startsWith('+') &&
    dialDigits &&
    digits.startsWith(dialDigits)
  ) {
    localNumber = digits.slice(dialDigits.length);
  }

  return {
    phone_number: localNumber,
    country_dial_code: dialDigits,
  };
}

export interface UpdateSignupReasonPayload {
  signup_reason: SignupReasonCode[];
}

export const updatePhoneNumber = async (
  payload: UpdatePhoneNumberPayload
): Promise<void> => {
  const response = await fetch('/api/v5/account/update_phone_number', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    await parseApiError(response);
  }
};

export const updateSignupReason = async (
  payload: UpdateSignupReasonPayload
): Promise<void> => {
  const response = await fetch('/api/v5/account/update_signup_reason', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    await parseApiError(response);
  }
};

async function submitAccountSetup(
  values: AccountSetupModalValues
): Promise<void> {
  const requests = [];

  if (
    (values.mode === 'both' || values.mode === 'phone') &&
    values.phoneNumber &&
    values.countryDialCode
  ) {
    requests.push(
      updatePhoneNumber(
        buildPhoneUpdatePayload(values.phoneNumber, values.countryDialCode)
      )
    );
  }

  if (
    (values.mode === 'both' || values.mode === 'signupReason') &&
    values.signupReasonCode !== undefined
  ) {
    requests.push(
      updateSignupReason({
        signup_reason: [values.signupReasonCode],
      })
    );
  }

  if (requests.length === 0) {
    return;
  }

  const results = await Promise.allSettled(requests);

  const rejection = results.find(
    (result): result is PromiseRejectedResult => result.status === 'rejected'
  );

  if (rejection) {
    throw rejection.reason instanceof Error
      ? rejection.reason
      : new Error(String(rejection.reason));
  }
}

export function useAccountSetup() {
  return useMutation<void, Error, AccountSetupModalValues>({
    mutationFn: (values) => submitAccountSetup(values),
  });
}
